// Die Freigabe – das Herzstück im Büro (E3, S3; Mockup /crm/freigabe/:id):
// eine Seite mit zwei Spalten, links die ganze Besichtigung, rechts das
// Material, das beim Scrollen stehen bleibt. „Nichts ist tief vergraben.“
//
// Wer nicht freigeben darf (Verkauf: die eigene; Montage, Inbetriebnahme:
// freigegebene), sieht nur die linke Spalte, über die ganze Breite.
//
// Zwei Bildschirme (Mockup, „Material in eigenem Fenster“): Der Knopf oben
// öffnet dieselbe Besichtigung unter #/freigabe/<id>/material in einem
// zweiten Fenster – dort nur das Material, ganze Höhe. Solange es offen ist,
// hat die Aufnahme hier die ganze Breite. Beide Fenster teilen den Entwurf
// (speicher.ts) und laden neu, wenn das andere freigibt oder ändert.
//
// Adressen: #/freigabe/<kennung> und #/freigabe/<kennung>/material

import { useEffect, useState } from "react";
import { darf, type CrmAufnahme } from "@happy-energy/kern";
import { Ikon, TestBand, Tipp, toastZeigen } from "@happy-energy/oberflaeche";
import { adresseZeigen, wannZeigen } from "../anzeige.ts";
import { Laedt, LadeFehler, useLaden } from "../laden.tsx";
import { nurEigene, startpfad } from "../navigation.ts";
import { Seite } from "../rahmen.tsx";
import { useNeuLadenSignal } from "../speicher.ts";
import { useCrm } from "../zusammenhang.tsx";
import { AufnahmeAnsicht } from "./Aufnahme.tsx";
import { Material } from "./Material.tsx";

/** Hält das zweite Fenster im Blick: Ist es zu, kommt die Material-Spalte zurück. */
function useMaterialFenster(id: string) {
  const [fenster, setFenster] = useState<Window | null>(null);
  useEffect(() => {
    if (!fenster) return;
    const uhr = setInterval(() => {
      if (fenster.closed) setFenster(null);
    }, 800);
    return () => clearInterval(uhr);
  }, [fenster]);

  function oeffnen() {
    // Fester Name je Besichtigung: ein zweiter Klick holt dasselbe Fenster nach vorne, statt ein drittes zu öffnen.
    const f = window.open(`#/freigabe/${id}/material`, `he-material-${id}`, "popup=yes,width=560,height=980");
    if (!f) {
      toastZeigen("Der Browser hat das Fenster blockiert – bitte Pop-ups für diese Seite erlauben.", "fehler");
      return;
    }
    f.focus();
    setFenster(f);
  }
  function zurueckholen() {
    fenster?.close();
    setFenster(null);
  }
  return { offen: fenster !== null, oeffnen, zurueckholen };
}

export function FreigabeSeite({ id }: { id: string }) {
  const { ich } = useCrm();
  const mitMaterial = darf(ich.nutzer.rolle, "freigeben");
  const { daten: a, fehler, neu } = useLaden<CrmAufnahme>(`/api/crm/aufnahme/${id}`);
  useNeuLadenSignal(id, neu);
  const fenster = useMaterialFenster(id);
  const wort = mitMaterial ? "Freigabe" : "Besichtigung";
  // Zurück dorthin, wo diese Rolle die Besichtigung gefunden hat: Büro und Leitung seit E3c, S2 „Zu tun“, der
  // Verkauf seine Besichtigungen; Montage und Inbetriebnahme kennen keine Liste davon.
  const rolle = ich.nutzer.rolle;
  const zurueck = darf(rolle, "zu-tun")
    ? { pfad: "/zu-tun", name: "Zu tun" }
    : darf(rolle, "aufnahmen-liste")
      ? { pfad: "/pruefen", name: nurEigene(rolle) ? "Meine Besichtigungen" : "Besichtigungen" }
      : { pfad: startpfad(rolle), name: "Zur Startseite" };
  const zweiSpalten = mitMaterial && !fenster.offen;

  return (
    <Seite
      // Die Fragen dieser Besichtigung zeigt die Seite selbst (Band und an jedem Stück) – das Band oben nennt nur die übrigen.
      fragenHier={[id]}
      titel={a ? `${wort} · ${a.kunde.name}` : wort}
      unter={a ? `${adresseZeigen(a.objekt)} · gesendet ${a.gesendetAm ? wannZeigen(a.gesendetAm) : "–"} · ${a.verkaeufer.name}` : undefined}
      knoepfe={
        mitMaterial && a ? (
          <>
            {/* Seit E3c, S2: zurück zu „Zu tun“ – dort geht es nach dem Freigeben weiter. */}
            <a href={`#${zurueck.pfad}`} className="crm-knopf rand klein">
              <Ikon name="links" groesse="k" /> {zurueck.name}
            </a>
            {/* Seit E3b (O72): die Unterlage gleich von hier – auch vor der Freigabe (dann als Entwurf). */}
            {darf(rolle, "unterlagen") && (
              <a href={`#/unterlage/${a.id}`} className="crm-knopf rand klein">
                <Ikon name="drucker" groesse="k" /> Unterlage ansehen
              </a>
            )}
            {fenster.offen ? (
              <button type="button" className="crm-knopf rand klein" onClick={fenster.zurueckholen}>
                <Ikon name="ebenen" groesse="k" /> Material zurück in dieses Fenster
              </button>
            ) : (
              <button type="button" className="crm-knopf rand klein" onClick={fenster.oeffnen}>
                <Ikon name="ebenen" groesse="k" /> Material in eigenem Fenster
              </button>
            )}
          </>
        ) : (
          <a href={`#${zurueck.pfad}`} className="crm-knopf rand klein">
            <Ikon name="links" groesse="k" /> {zurueck.name}
          </a>
        )
      }
    >
      {fehler !== undefined && <LadeFehler fehler={fehler} nochmal={neu} />}
      {!a && fehler === undefined && <Laedt text="Die Besichtigung wird geladen …" />}
      {a && (
        <div className="freigabe-huelle">
          {zweiSpalten && (
            // Nur sichtbar, wenn die Spalten untereinander stehen (crm.css, Abfrage am Behälter).
            <button type="button" className="crm-knopf lime klein zum-material" onClick={zumMaterial}>
              <Ikon name="paket" groesse="k" /> Zum Material
            </button>
          )}
          <div className={`freigabe${zweiSpalten ? "" : " eine-spalte"}`}>
            <AufnahmeAnsicht a={a} neuLaden={neu} />
            {zweiSpalten && (
              <div className="material-spalte" id="material">
                <Material a={a} neuLaden={neu} />
              </div>
            )}
          </div>
          {mitMaterial && fenster.offen && (
            <Tipp art="grau" ikon="ebenen">
              Das Material ist im eigenen Fenster offen. Schließt du es, kommt es hierher zurück.
            </Tipp>
          )}
        </div>
      )}
    </Seite>
  );
}

/**
 * Ein weicher Sprung zum Material – wenn die Spalten untereinander stehen
 * (schmaler Bildschirm). Ein Knopf, kein Link „#material“: Das # gehört
 * hier der Navigation, ein Link würde die Seite „Standardmaterial“ öffnen.
 */
function zumMaterial() {
  document.getElementById("material")?.scrollIntoView({ behavior: "smooth", block: "start" });
}

/**
 * Nur das Material, ganze Höhe – das zweite Fenster (#/freigabe/<id>/material).
 * Ohne Seitenleiste; oben Kunde und Adresse, damit klar ist, wozu es gehört.
 */
export function MaterialFenster({ id }: { id: string }) {
  const { ich } = useCrm();
  const { daten: a, fehler, neu } = useLaden<CrmAufnahme>(`/api/crm/aufnahme/${id}`);
  useNeuLadenSignal(id, neu);

  useEffect(() => {
    document.title = a ? `Material · ${a.kunde.name} · Happy Energy CRM` : "Material · Happy Energy CRM";
  }, [a]);

  return (
    <div className={`crm-huelle material-fenster${ich.umgebung === "echt" ? "" : " mit-band"}`}>
      <TestBand umgebung={ich.umgebung} />
      <header className="material-fenster-kopf">
        <div>
          <b>{a ? a.kunde.name : "Material"}</b>
          {a && <span>{adresseZeigen(a.objekt)}</span>}
        </div>
        <button type="button" className="crm-knopf rand klein" onClick={() => window.close()}>
          <Ikon name="kreuz" groesse="k" /> Fenster schließen
        </button>
      </header>
      <div className="material-fenster-inhalt">
        {fehler !== undefined && <LadeFehler fehler={fehler} nochmal={neu} />}
        {!a && fehler === undefined && <Laedt />}
        {a && <Material a={a} neuLaden={neu} />}
      </div>
    </div>
  );
}
